import moment from "moment";
import React from "react";
import {Button, CheckBox, Column, Row, Select, Spinner, Text} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {ShowMessageBox} from "react-vmessagebox";
import {minuteInMS, Observer, observer_simple, RunInAction, RunInAction_Set, TextPlus, VDateTime, useForceUpdate, InfoButton} from "web-vcore";
import {GetLiveLocalSession_Reactive} from "../../../../Engine/LiveSessionManagement.js";
import {DialogStyle} from "../../../../Utils/UI/GlobalStyles.js";
import {AlarmsComp, GetAlarmDelayCandidates} from "../../../../Engine/LiveSession/Components/AlarmsComp.js";
import {GetSettingNameFromGetter} from "../../../../Server/DB/sessions/@Session.js";
import {KEY_alarmRestartTimer_getInterval} from "../../../../Engine/LiveSession/Components/AlarmComps/@AlarmComp.js";
import {ModifyIntervalOfPossiblyActiveTimer} from "../../../../Utils/General/General.js";
import {observer_mgl} from "mobx-graphlink";
import {GetIndexesOfDriftingEndEvents} from "../../../../Server/DB/@Shared/SessionPeriodUtils.js";
import {currentBelt} from "../../../Tools/@Shared/BeltInterface.js";
import {BreathComp} from "../../../../Engine/LiveSession/Components/BreathComp.js";
import {TriggerPackage} from "../../../../Engine/LiveSession.js";
import {ScrollView} from "react-vscrollview";
import {store} from "../../../../Store/index.js";
import {Phase} from "../../../../Server/DB/@Shared/Phase.js";

export const SessionControlsUI = observer_mgl(()=>{
	// hooks first
	const forceUpdate = useForceUpdate();

	const liveLocalSession = GetLiveLocalSession_Reactive();
	if (liveLocalSession == null) return null;
	const liveAlarmsComp = liveLocalSession.Comp(AlarmsComp);
	//const sleepEnderTime = liveLocalSession?.coreData.alarms_sleepEnderActivatableFrom;
	const coreData = liveLocalSession.coreData;

	const restartSleepTimerWithDelayX = (delayInMS: number)=>{
		if (liveLocalSession == null || liveAlarmsComp == null) return;
		if (liveAlarmsComp.sleepUntilAlarmTimer.Enabled) {
			liveAlarmsComp.sleepUntilAlarmTimer.Start(delayInMS);
		}
		// this case isn't technically needed, but it's helpful to quickly resume engine-usage after prior session ended too early
		else if (liveLocalSession.initialDelayTimer.Enabled) {
			liveLocalSession.initialDelayTimer.Start(delayInMS);
		}
	};

	const alarmDelayCandidates = GetAlarmDelayCandidates(liveAlarmsComp.c);
	return (
		<Column>
			{liveLocalSession.c.breath.enabled && <>
				<Row mt={5}>
					<Button p="5px 10px" text="Reconnect belt" onClick={async()=>{
						try {
							if (currentBelt.beltStatus != "disconnected" || currentBelt.device != null) {
								await currentBelt.Disconnect();
							}
							await currentBelt.Connect();
							forceUpdate();
						} catch (error) {
							console.error("Failed to reconnect belt:", error);
						}
					}}/>
				</Row>
			</>}
			{liveLocalSession.autoEndDelayTimer.Enabled &&
			<Row ml={10} mt={5}>
				<Text>{`Change auto-end time (${moment(liveLocalSession.autoEndDelayTimer?.nextTickTime).format("HH:mm:ss")})`}</Text>
				<Button ml={5} p="1px 7px" text="-30m" onClick={async()=>{
					liveLocalSession.ModifyLiveAutoEndTime(liveLocalSession.autoEndDelayTimer.nextTickTime! - (30 * minuteInMS));
					forceUpdate();
				}}/>
				<Button ml={5} p="1px 7px" text="+30m" onClick={async()=>{
					liveLocalSession.ModifyLiveAutoEndTime(liveLocalSession.autoEndDelayTimer.nextTickTime! + (30 * minuteInMS));
					forceUpdate();
				}}/>
			</Row>}
			{liveAlarmsComp?.c.enabled && <>
				{alarmDelayCandidates.length > 0 &&
					<Row mt={5}>
						<Text>{`Change alarm delay (${Math.ceil((liveAlarmsComp.sleepUntilAlarmTimer.intervalInMS / minuteInMS))}m)`}</Text>
						{GetAlarmDelayCandidates(liveAlarmsComp.c).map(durationInMins=>{
							return <Button key={durationInMins} ml={5} p="1px 7px" text={`${durationInMins}m`} onClick={async()=>{
								ModifyIntervalOfPossiblyActiveTimer(
									liveAlarmsComp.sleepUntilAlarmTimer, durationInMins * minuteInMS,
									interval=>liveAlarmsComp.SetSleepUntilAlarmTimerDuration(interval), // use custom interval-setter (since Set...Duration func has some extra effects)
								);
								forceUpdate();
							}}/>;
						})}
					</Row>}
				<Row mt={5}>
					<Text>Current timer:</Text>
					<Button ml={5} p="1px 7px" text="Set to X" onClick={()=>{
						if (!liveAlarmsComp.sleepUntilAlarmTimer.Enabled) return;
						if (liveAlarmsComp.sleepUntilAlarmTimer.nextTickTime == null) return;
						const timerTimeLeft = liveAlarmsComp.sleepUntilAlarmTimer.nextTickTime - Date.now();
						let newMinutesLeft = Math.ceil(timerTimeLeft / minuteInMS);
						const controller = ShowMessageBox({
							title: "Set sleep-timer to X", cancelButton: true,
							message: ()=>(
								<Column style={{padding: `10px 0`, width: 600}}>
									<Row>
										<Text>Minutes left:</Text>
										<Spinner ml={5} value={newMinutesLeft} onChange={val=>{
											newMinutesLeft = val;
											controller.UpdateUI();
										}}/>
									</Row>
								</Column>
							),
							onOK: ()=>{
								restartSleepTimerWithDelayX(newMinutesLeft * minuteInMS);
							},
						});
					}}/>
					<Button ml={5} p="1px 7px" text={`Reset to ${liveLocalSession.coreData.journey_alarmDelay / minuteInMS}m`} onClick={()=>{
						restartSleepTimerWithDelayX(liveLocalSession.coreData.journey_alarmDelay);
					}}/>
				</Row>
			</>}
			<Row mt={5}>
				<Button p="5px 10px" text="Susp. comps" onClick={()=>{
					const controller = ShowMessageBox({
						title: "Suspend/unsuspend comps",
						message: ()=>(
							<Column style={DialogStyle({width: 250})}>
								{liveLocalSession.components.map((comp, index)=>{
									return <Row key={index}>
										<CheckBox ml={5} text={comp.constructor.name} value={!comp.IsSuspended()} onChange={val=>{
											if (val) comp.Unsuspend();
											else comp.Suspend();
											controller.UpdateUI();
										}}/>
									</Row>;
								})}
							</Column>
						),
					});
				}}/>
				<Button ml={5} p="5px 10px" text="Alarm seq." onClick={()=>{
					const controller = ShowMessageBox({
						title: "Set alarms main-sequence",
						message: observer_simple(()=>{
							const sequenceOptions = [{name: "", value: null as string|n}].concat(
								(liveLocalSession.c.alarms.phaseAlarm_sequences ?? []).map(a=>({name: a.name, value: a.name}))
							);
							return (
								<Column style={DialogStyle({width: 350})}>
									<Row>Note: Alarm phase must start (again) for changes to main-sequence to apply.</Row>
									<Row>
										<Select options={sequenceOptions} value={liveLocalSession?.coreData.alarms_currentSequence} onChange={(val: string|n)=>{
											const alarmsComp = liveLocalSession?.Comp(AlarmsComp);
											if (alarmsComp) {
												const sequence = alarmsComp.c.phaseAlarm_sequences.find(a=>a.name == val);
												alarmsComp.SetAlarmCurrentSequence(sequence);
											}
										}}/>
									</Row>
								</Column>
							);
						}),
					});
				}}/>
				<Button ml={5} p="5px 10px" text="Triggers" onClick={()=>{
					const uiState = store.main.engine.journey;
					const controller = ShowMessageBox({
						title: "Registered triggers",
						message: observer_simple(()=>{
							//const isDisabled = (pack: TriggerPackage)=>pack.activator == null;
							const hasHotkey = (pack: TriggerPackage)=>pack.condition != null && pack.condition.sequences.some(s=>s.items.length > 0);
							const hotkeyEnabled = (pack: TriggerPackage)=>{
								if (!hasHotkey(pack)) return false;
								if (pack.condition!.disabled) return false;
								// also consider hotkey "disabled" if it has no activator (as can happen if eg. comp is disabled at time of session start)
								if (pack.activator == null) return false;
								return true;
							};
							const hotkeyChar = (pack: TriggerPackage)=>hasHotkey(pack) ? (hotkeyEnabled(pack) ? " ●" : " ○") : "";
							const byComp = new Map<string, TriggerPackage[]>();
							for (const pack of liveLocalSession.triggerPackages) {
								const compName = pack.comp.constructor.name;
								if (!byComp.has(compName)) byComp.set(compName, []);
								byComp.get(compName)!.push(pack);
							}
							return (
								<ScrollView style={DialogStyle({width: 800})}>
									<Row>
										<Text>Hide triggers:</Text>
										<CheckBox ml={10} text="Disabled" value={uiState.triggersDialog_hideDisabled} onChange={val=>{
											RunInAction_Set(()=>uiState.triggersDialog_hideDisabled = val);
											controller.UpdateUI();
										}}/>
										<CheckBox ml={10} text="Unassigned" value={uiState.triggersDialog_hideUnassigned} onChange={val=>{
											RunInAction_Set(()=>uiState.triggersDialog_hideUnassigned = val);
											controller.UpdateUI();
										}}/>
										<CheckBox ml={10} text="Comp-inactive" value={uiState.triggersDialog_hideCompInactive} onChange={val=>{
											RunInAction_Set(()=>uiState.triggersDialog_hideCompInactive = val);
											controller.UpdateUI();
										}}/>
										<Text ml={10} style={{fontSize: 12, opacity: .7}}>Note: ● = has hotkey (enabled), ○ = has hotkey (disabled)</Text>
										<InfoButton ml={5} text="Suspension-states are ignored for enabled/disabled states here."/>
									</Row>
									{[...byComp.entries()].map(([compName, packs])=>{
										let visiblePacks = packs.slice();
										if (uiState.triggersDialog_hideDisabled) visiblePacks = visiblePacks.filter(a=>hotkeyEnabled(a));
										if (uiState.triggersDialog_hideUnassigned) visiblePacks = visiblePacks.filter(a=>hasHotkey(a));
										if (uiState.triggersDialog_hideCompInactive) visiblePacks = visiblePacks.filter(a=>a.comp.active);
										if (visiblePacks.length == 0) return null;
										const compName_noCompSuffix = compName.replace("Comp", "");
										return (
											<Row key={compName} mt={5} style={{flexWrap: "wrap", gap: 5}}>
												<Text style={{fontWeight: "bold"}}>{compName_noCompSuffix}:</Text>
												{visiblePacks.map(pack=>{
													const triggerName_noCompSuffix = pack.name.replace(compName_noCompSuffix + "_", "")
													const disabled = !hotkeyEnabled(pack);
													return (
														<Button key={pack.name} mt={3} p="3px 8px"
														text={triggerName_noCompSuffix + hotkeyChar(pack)}
															style={disabled ? {opacity: 0.7} : undefined}
															onClick={()=>pack.actionIfLocal?.(null as any)}
														/>
													);
												})}
											</Row>
										);
									})}
								</ScrollView>
							);
						}),
					});
				}}/>
				<Button ml={5} p="5px 10px" text="Dyn. settings" onClick={()=>{
					const controller = ShowMessageBox({
						title: "Dynamic settings",
						message: observer_simple(()=><Column style={DialogStyle({width: 350})}>
							{Object.entries(liveLocalSession.coreData.settingValuesSelected ?? {}).map(([key, value], index)=>{
								const Change = (..._)=>controller.UpdateUI();
								return (
									<Row key={key} mt={index == 0 ? 0 : 5}>
										<Text style={{flex: 50, minWidth: 0, wordBreak: "break-word"}}>{key}:</Text>
										{typeof value.value == "number" &&
										<Spinner ml={5} step="any" value={value.value} onChange={val=>{
											// not sure if this wrapper is needed actually...
											RunInAction("DynamicSettingsPanel", ()=>{
												value.value = val;
												if (key == GetSettingNameFromGetter(a=>a.general.globalAlarmRestartIntervalMultiplier) && liveLocalSession) {
													for (const timer of liveLocalSession.timerContext.timers) {
														if (timer[KEY_alarmRestartTimer_getInterval]) {
															const newInterval = timer[KEY_alarmRestartTimer_getInterval]();
															ModifyIntervalOfPossiblyActiveTimer(timer, newInterval);
														}
													}
												}
												Change();
											});
										}}/>}
										{typeof value.value != "number" &&
										<Text style={{flex: 50, minWidth: 0}}>{value.value}</Text>}
									</Row>
								);
							})}
						</Column>),
					});
				}}/>
			</Row>
			<Row mt={5}>
				<Text>Set phase:</Text>
				<Button ml={5} p="1px 7px" text="[solve]" onClick={()=>{
					if (liveAlarmsComp.DoesPhaseSupportSolveProgress() && liveAlarmsComp.DoesStateAllowSolveCompletion()) {
						liveAlarmsComp.NotifySolveCompleted(true, "Solve was completed (from press of [solve] button); trying to entering phase drifting.");
					}
				}}/>
				<Button ml={5} p="1px 7px" text="Drift" onClick={()=>{
					liveAlarmsComp.SetPhase(Phase.drifting);
				}}/>
				<Button ml={5} p="1px 7px" text="Drift (as if no wake)" onClick={()=>{
					let lastResleepEndEvent_index = GetIndexesOfDriftingEndEvents(liveLocalSession.coreData.events).LastOrX() ?? -1;
					const actionValid = ()=>liveAlarmsComp.PhaseIs(Phase.dreaming, Phase.solving, Phase.drifting) && lastResleepEndEvent_index != -1;
					if (!actionValid()) return;

					const eventsSinceResleepEnd = liveLocalSession.coreData.events.slice(lastResleepEndEvent_index + 1);
					ShowMessageBox({
						title: "Set phase to sleep, and remove events?", cancelButton: true,
						message: `This action may cause some data-loss, by removing the last Journey.ResleepEnd event, and all events since then (${eventsSinceResleepEnd.length}).`,
						onOK: ()=>{
							lastResleepEndEvent_index = lastResleepEndEvent_index = GetIndexesOfDriftingEndEvents(liveLocalSession.coreData.events).LastOrX() ?? -1;
							if (!actionValid()) return;

							liveAlarmsComp.SetPhase(Phase.drifting);
							const removeEventsStartingFrom = lastResleepEndEvent_index;
							liveLocalSession.DeleteEventRange(removeEventsStartingFrom);
						},
					});
				}}/>
				<Button ml={5} p="1px 7px" text="Dream" onClick={()=>{
					liveAlarmsComp.SetPhase(Phase.dreaming);
				}}/>
			</Row>
			<Row mt={5}>
				<TextPlus info={`
					If set, after the given time, the following effects will occur:
					1) Phase will be set to "Alarm". (even if was already in "Alarm" phase -- in order to trigger all alarm-start effects)
					2) Alarm-sequence selection will always pick the one named "SleepEnder" (exactly), even if it's otherwise disabled.
					3) "Solving" of the alarm phase will be impossible. (albeit, solve "progress" still has effects, eg. as for micro-snooze feature)
				`.AsMultiline(0)}>Sleep ender:</TextPlus>
				<div style={{marginLeft: 5}}/>
				{/* Why date enabled? It bugs otherwise, where de-focusing input sends two onChange events (first valid, second null). */}
				<VDateTime dateFormat="YYYY-MM-DD" timeFormat="HH:mm:ss" value={coreData.alarms_sleepEnderActivatableFrom ? moment(coreData.alarms_sleepEnderActivatableFrom) : null} onChange={val=>{
					if (val == null) {
						RunInAction_Set(()=>coreData.alarms_sleepEnderActivatableFrom = null);
						return;
					}
					while (val.valueOf() < coreData.startTime) val = val.clone().add(1, "day");
					RunInAction_Set(()=>coreData.alarms_sleepEnderActivatableFrom = val!.valueOf());
				}}/>
				<Button ml={5} p="1px 7px" text="Now" onClick={()=>RunInAction_Set(()=>coreData.alarms_sleepEnderActivatableFrom = Date.now())}/>
			</Row>
			<Row mt={5}>
				<TextPlus info={`
					If target minute reached (must be >0), when reality-check reminder plays, its text will also include the phrase ", near".
				`.AsMultiline(0)}>Stay awake, warn:</TextPlus>
				<Spinner ml={5} value={coreData.realityCheck_wakerWarnFrom ?? -1} onChange={val=>RunInAction_Set(()=>coreData.realityCheck_wakerWarnFrom = val)}/>
				<TextPlus ml={5} info={`
					If target minute reached (must be >0), when reality-check reminder plays, it will be followed with the playing of reality-check comp's "waker effect".
				`.AsMultiline(0)}>Wake:</TextPlus>
				<Spinner ml={5} value={coreData.realityCheck_wakerEffectFrom ?? -1} onChange={val=>RunInAction_Set(()=>coreData.realityCheck_wakerEffectFrom = val)}/>
			</Row>
			<Row>
				<Text>Till alarm-end, block:</Text>
				<CheckBox ml={5} text="Lights" value={coreData.alarms_tillAlarmEnd_blockLightEffects ?? false} onChange={val=>RunInAction_Set(()=>coreData.alarms_tillAlarmEnd_blockLightEffects = val)}/>
				<CheckBox ml={5} text="Micro-snoozing" value={coreData.alarms_tillAlarmEnd_blockMicroSnooze ?? false} onChange={val=>RunInAction_Set(()=>coreData.alarms_tillAlarmEnd_blockMicroSnooze = val)}/>
			</Row>
			<Row>
				<TextPlus info={`
					If we've been in the alarm-or-solving phases for X+ minutes, without awake-signals for X+ minutes, then...
					* End the alarm (without solving) and go to sleep phase. (with X minutes subtracted from the regular sleep-duration)
					This one-time effect ends when phase exits from union of alarm/solving. (normally, from this alarm-end effect executing!)
				`.AsMultiline(0)}>One-time alarm-end, if no awake-signal for:</TextPlus>
				<Spinner ml={5} value={coreData.alarms_tillAlarmEnd_endAlarmAfterXMinutesOfNoAwakeSignal ?? -1} onChange={val=>{
					RunInAction_Set(()=>coreData.alarms_tillAlarmEnd_endAlarmAfterXMinutesOfNoAwakeSignal = val);
					liveAlarmsComp.UpdateSolveGiveUpTimer(false, false, true);
				}}/>
				<Text>m</Text>
			</Row>
		</Column>
	);
});